import React, { useMemo, useState, useEffect } from "react";
import { ListChecks, ExternalLink, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import {
  getList,
  APP_STATUSES,
  setApplicationStatus,
  removeApplication,
} from "@/lib/storage";

const STATUS_COLORS = {
  applied: "#FFC400",
  screen: "#0a0a0a",
  interview: "#00FF66",
  offer: "#E10600",
  rejected: "#0a0a0a",
};

const STATUS_LABEL = {
  applied: "Applied",
  screen: "Screen",
  interview: "Interview",
  offer: "Offer",
  rejected: "Rejected",
};

export default function DashboardApplications() {
  const [list, setListState] = useState(getList("apps"));
  useEffect(() => {
    const h = () => setListState(getList("apps"));
    window.addEventListener("jh:storage", h);
    return () => window.removeEventListener("jh:storage", h);
  }, []);

  const counts = useMemo(() => {
    const c = {};
    APP_STATUSES.forEach((s) => (c[s] = 0));
    list.forEach((a) => (c[a.status] = (c[a.status] || 0) + 1));
    return c;
  }, [list]);

  const onStatus = (url, s) => {
    setApplicationStatus(url, s);
    setListState(getList("apps"));
  };
  const onRemove = (url) => {
    removeApplication(url);
    setListState(getList("apps"));
  };

  return (
    <div data-testid="dash-applications" className="space-y-6">
      <div>
        <h1 className="font-display text-3xl md:text-4xl flex items-center gap-3">
          <ListChecks className="text-[#E10600]" /> APPLICATIONS
        </h1>
        <p className="font-mono-body text-sm text-[#0a0a0a]/65 mt-1.5 max-w-xl">
          Every application you fire off from the Job Directory lands here. Track them applied → screen → interview → offer.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {APP_STATUSES.map((s) => (
          <div
            key={s}
            data-testid={`stat-${s}`}
            className="bg-white border-2 border-[#0a0a0a] shadow-brutal-sm p-3"
          >
            <div className="font-accent text-[10px]" style={{ color: STATUS_COLORS[s] }}>
              {STATUS_LABEL[s]}
            </div>
            <div className="font-display text-3xl mt-0.5">{counts[s] || 0}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border-4 border-[#0a0a0a] shadow-brutal">
        <div className="grid grid-cols-12 gap-3 px-4 md:px-5 py-3 bg-[#0a0a0a] text-[#FFC400] font-accent text-[10px]">
          <div className="col-span-12 md:col-span-5">ROLE</div>
          <div className="col-span-6 md:col-span-3">STATUS</div>
          <div className="col-span-6 md:col-span-2">APPLIED</div>
          <div className="col-span-6 md:col-span-2 text-right">ACTIONS</div>
        </div>
        {list.length === 0 ? (
          <div className="px-6 py-10 text-center font-mono-body text-sm text-[#0a0a0a]/65">
            No applications yet — fire one off from the <Link to="/dashboard/jobs" className="underline">Job Directory</Link>.
          </div>
        ) : (
          list.map((a) => (
            <div
              key={a.url}
              data-testid="app-row"
              className="grid grid-cols-12 gap-3 items-center px-4 md:px-5 py-3 border-b-2 border-[#0a0a0a]/10 last:border-b-0"
            >
              <div className="col-span-12 md:col-span-5 min-w-0">
                <div className="font-display text-[14px] leading-tight truncate">{a.title}</div>
                <div className="font-mono-body text-[11px] text-[#0a0a0a]/65 truncate">
                  {a.company} · {a.location || "—"}
                </div>
              </div>
              <div className="col-span-12 md:col-span-3 flex flex-wrap gap-1.5">
                {APP_STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => onStatus(a.url, s)}
                    data-testid={`app-status-${s}`}
                    className={`font-accent text-[10px] border-2 border-[#0a0a0a] px-2 py-1 ${
                      a.status === s
                        ? "bg-[#0a0a0a] text-[#FFC400]"
                        : "bg-white"
                    }`}
                  >
                    {STATUS_LABEL[s]}
                  </button>
                ))}
              </div>
              <div className="col-span-6 md:col-span-2 font-mono-body text-[11px] text-[#0a0a0a]/65">
                {a.appliedAt ? new Date(a.appliedAt).toLocaleDateString() : "—"}
              </div>
              <div className="col-span-6 md:col-span-2 flex items-center justify-end gap-1.5">
                <button
                  onClick={() => onRemove(a.url)}
                  aria-label="Remove"
                  className="hover-pop w-9 h-9 border-2 border-[#0a0a0a] bg-white inline-flex items-center justify-center shadow-brutal-sm"
                >
                  <Trash2 size={13} strokeWidth={3} />
                </button>
                <a
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover-pop bg-[#E10600] text-white border-2 border-[#0a0a0a] px-3 py-2 font-accent text-[11px] shadow-brutal-sm inline-flex items-center gap-1.5"
                >
                  Open <ExternalLink size={11} strokeWidth={3} />
                </a>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
